/**
 * Preferences Dialog
 * 
 * A dialog for managing user preferences: timezone, week start day, and theme.
 * Also includes account management options in a danger zone section.
 */

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ClockIcon, CalendarIcon, DisplayIcon, Sun, Moon, Monitor, ArrowLeft, TrashIcon } from '@/components/icons'
import { Loader } from '@/components/ai-elements/loader'
import { useSettings } from '@/context/settings-context'
import { useAuth } from '@/context/auth-context'
import { useApi } from '@/hooks/useApi'
import { TIMEZONE_GROUPS } from '@/types/timezone'
import { toast } from 'sonner'

interface PreferencesDialogProps {
  open: boolean
  onClose: () => void
}

export function PreferencesDialog({ open, onClose }: PreferencesDialogProps) {
  const { timezone, weekStartsOn, theme, setTimezone, setWeekStartsOn, setTheme } = useSettings()
  const { signOut } = useAuth()
  const { post } = useApi()
  
  // Delete account confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDeleteAccount = async () => {
    setDeleting(true)
    try {
      const response = await post<{ success: boolean; error?: string }>(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/account-delete`,
        {}
      )

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to delete account')
      }

      toast.success('Account deleted successfully')
      setDeleteConfirmOpen(false)
      
      // Sign out the user after successful deletion
      await signOut()
    } catch (error) {
      console.error('Failed to delete account:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete account')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
    
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent 
        className="
          max-w-none
          p-0
          border-0 shadow-none
          w-full h-[80vh] max-h-[80vh]
          md:w-[600px] md:h-auto md:max-h-[70vh]
          flex flex-col
          fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
          md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
          rounded-t-3xl md:rounded-lg
        "
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogDescription className="sr-only">Manage your timezone, week start, and theme preferences.</DialogDescription>
        <DialogHeader className="px-2 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            {/* Back Button - Icon only on mobile, icon + text on desktop */}
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden md:inline">Back</span>
            </Button>
            
            <DialogTitle className="flex-1 text-center">Preferences</DialogTitle>
            
            {/* Spacer to balance the layout */}
            <div className="w-[68px] md:w-[76px]"></div>
          </div>
        </DialogHeader>
        
        <div className="px-2 pb-6 overflow-y-auto overflow-x-auto flex-1 min-h-0">
          <div className="space-y-6">
            {/* Preferences Card */}
            <Card className="overflow-hidden">
              <CardContent className="p-6 space-y-6">
                {/* Timezone Setting */}
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <ClockIcon className="h-6 w-6 text-muted-foreground flex-shrink-0" />
                    <div className="font-medium">Timezone</div>
                  </div>
                  <div className="w-full md:w-64">
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger className="shadow-none">
                        <SelectValue placeholder="Select a timezone" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectScrollUpButton />
                        {TIMEZONE_GROUPS.map((group) => (
                          <SelectGroup key={group.label}>
                            <SelectLabel>{group.label}</SelectLabel>
                            {group.timezones.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                        <SelectScrollDownButton />
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Week Start Setting */}
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <CalendarIcon className="h-6 w-6 text-muted-foreground flex-shrink-0" />
                    <div className="font-medium">Week starts on</div>
                  </div>
                  <div className="w-full md:w-32">
                    <Select value={weekStartsOn} onValueChange={(value: 'sunday' | 'monday') => setWeekStartsOn(value)}>
                      <SelectTrigger className="shadow-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sunday">Sunday</SelectItem>
                        <SelectItem value="monday">Monday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Theme Setting */}
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <DisplayIcon className="h-6 w-6 text-muted-foreground flex-shrink-0" />
                    <div className="font-medium">Theme</div>
                  </div>
                  <div className="w-full md:w-32">
                    <Select value={theme} onValueChange={(value: 'light' | 'dark' | 'system') => setTheme(value)}>
                      <SelectTrigger className="shadow-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">
                          <div className="flex items-center gap-2">
                            <Sun className="h-4 w-4" />
                            Light
                          </div>
                        </SelectItem>
                        <SelectItem value="dark">
                          <div className="flex items-center gap-2">
                            <Moon className="h-4 w-4" />
                            Dark
                          </div>
                        </SelectItem>
                        <SelectItem value="system">
                          <div className="flex items-center gap-2">
                            <Monitor className="h-4 w-4" />
                            System
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Danger Zone Card */}
            <Card className="overflow-hidden border-destructive/50">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold text-destructive">Danger zone</h3>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="text-sm text-muted-foreground">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="flex items-center gap-2 flex-shrink-0"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete account
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Delete Account Confirmation Dialog */}
    <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <DialogContent className="max-w-md" hideClose>
        <DialogHeader>
          <DialogTitle className="text-destructive">Delete Account</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete your account? This will permanently remove all your data including:
          </DialogDescription>
        </DialogHeader>
        <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 ml-2">
          <li>All calendar integrations</li>
          <li>All automation rules</li>
          <li>All life domains</li>
          <li>All analytics data</li>
        </ul>
        <p className="text-sm font-medium text-destructive">This action cannot be undone.</p>
        <div className="flex justify-center gap-3 mt-4">
          <Button
            variant="outline"
            onClick={() => setDeleteConfirmOpen(false)}
            className="w-32"
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteAccount}
            disabled={deleting}
            className="w-32"
          >
            {deleting ? (
              <>
                <Loader size={16} className="mr-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}

